import { ensureEntraServerSession } from "@/lib/auth/entra/browser-actions";

export type KnowledgeLakeUploadOptions = {
    sourceLabel?: string;
    companyId?: string;
    docType?: string;
    periodLabel?: string;
    /** When true, wait for server-side ingest before returning document IDs. */
    sync?: boolean;
};

export type KnowledgeLakeUploadResult = {
    accepted: string[];
    documentIds: string[];
};

/** Max single-file size aligned with ingress proxy-body-size (50m). */
export const KNOWLEDGE_UPLOAD_MAX_BYTES = 50 * 1024 * 1024;

export const KNOWLEDGE_UPLOAD_ACCEPTED_EXTENSIONS = [
    ".pdf",
    ".doc",
    ".docx",
    ".ppt",
    ".pptx",
    ".xls",
    ".xlsx",
] as const;

export const KNOWLEDGE_UPLOAD_MAX_PARALLEL = 3;

export const KNOWLEDGE_UPLOAD_POLL_INTERVAL_MS = 2000;

export function knowledgeFileKey(file: File): string {
    return `${file.name}:${file.size}:${file.lastModified}`;
}

export function isKnowledgeUploadExtensionAllowed(fileName: string): boolean {
    const lower = fileName.toLowerCase();
    return KNOWLEDGE_UPLOAD_ACCEPTED_EXTENSIONS.some((ext) => lower.endsWith(ext));
}

export function validateKnowledgeUploadFile(file: File): string | null {
    if (file.size === 0) {
        return "File is empty";
    }
    if (file.size > KNOWLEDGE_UPLOAD_MAX_BYTES) {
        return `"${file.name}" is too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Maximum size is 50 MB.`;
    }
    if (!isKnowledgeUploadExtensionAllowed(file.name)) {
        return "Unsupported file type";
    }
    return null;
}

export function formatUploadError(json: { error?: string }, status: number): string {
    const raw = json.error ?? `Upload failed (${status})`;
    if (raw.includes("Failed to parse body as FormData")) {
        return "Upload failed — the file could not be sent to the server. Try a smaller file or refresh and retry.";
    }
    if (status === 401) {
        return "Session expired — please sign in again";
    }
    return raw;
}

function buildUploadFormData(file: File, opts: KnowledgeLakeUploadOptions): FormData {
    const form = new FormData();
    form.append("files", file);
    form.set("sourceLabel", opts.sourceLabel ?? "Manual upload");
    if (opts.companyId) form.set("companyId", opts.companyId);
    if (opts.docType) form.set("docType", opts.docType);
    if (opts.periodLabel) form.set("periodLabel", opts.periodLabel);
    if (opts.sync) form.set("sync", "1");
    return form;
}

function parseUploadResponse(
    xhr: XMLHttpRequest,
): { ok: true; result: KnowledgeLakeUploadResult } | { ok: false; error: string } {
    let json: { success?: boolean; error?: string; accepted?: string[]; documentIds?: string[] } = {};
    try {
        json = JSON.parse(xhr.responseText) as typeof json;
    } catch {
        json = {};
    }

    if (xhr.status >= 200 && xhr.status < 300 && json.success) {
        return {
            ok: true,
            result: {
                accepted: Array.isArray(json.accepted) ? json.accepted : [],
                documentIds: Array.isArray(json.documentIds) ? json.documentIds : [],
            },
        };
    }

    return { ok: false, error: formatUploadError(json, xhr.status) };
}

export type UploadProgressCallbacks = {
    onProgress?: (percent: number) => void;
    signal?: AbortSignal;
};

/** Upload one file with byte-level progress via XMLHttpRequest. */
export async function uploadSingleFileWithProgress(
    file: File,
    options: KnowledgeLakeUploadOptions = {},
    callbacks: UploadProgressCallbacks = {},
): Promise<KnowledgeLakeUploadResult> {
    const sessionOk = await ensureEntraServerSession();
    if (!sessionOk) {
        throw new Error("Session expired — please sign in again");
    }

    const form = buildUploadFormData(file, options);

    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        const onAbort = () => xhr.abort();
        if (callbacks.signal) {
            if (callbacks.signal.aborted) {
                reject(new DOMException("Aborted", "AbortError"));
                return;
            }
            callbacks.signal.addEventListener("abort", onAbort, { once: true });
        }

        xhr.upload.onprogress = (event) => {
            if (!event.lengthComputable) return;
            const percent = Math.min(99, Math.round((event.loaded / event.total) * 100));
            callbacks.onProgress?.(percent);
        };

        xhr.onload = () => {
            callbacks.signal?.removeEventListener("abort", onAbort);
            const parsed = parseUploadResponse(xhr);
            if (parsed.ok) {
                callbacks.onProgress?.(100);
                resolve(parsed.result);
            } else {
                reject(new Error(parsed.error));
            }
        };

        xhr.onerror = () => {
            callbacks.signal?.removeEventListener("abort", onAbort);
            reject(new Error("Upload failed — check your connection"));
        };

        xhr.onabort = () => {
            callbacks.signal?.removeEventListener("abort", onAbort);
            reject(new DOMException("Aborted", "AbortError"));
        };

        xhr.open("POST", "/api/platform/knowledge/upload");
        xhr.withCredentials = true;
        xhr.send(form);
    });
}

async function uploadSingleFileToKnowledgeLake(
    file: File,
    opts: KnowledgeLakeUploadOptions,
): Promise<KnowledgeLakeUploadResult> {
    return uploadSingleFileWithProgress(file, opts);
}

/** Upload files sequentially (one multipart request per file). */
export async function uploadFilesToKnowledgeLake(
    files: File[],
    options: KnowledgeLakeUploadOptions | string = "Manual upload",
): Promise<KnowledgeLakeUploadResult> {
    if (!files.length) {
        return { accepted: [], documentIds: [] };
    }

    const opts: KnowledgeLakeUploadOptions =
        typeof options === "string" ? { sourceLabel: options } : options;

    const accepted: string[] = [];
    const documentIds: string[] = [];

    for (const file of files) {
        const validationError = validateKnowledgeUploadFile(file);
        if (validationError) {
            throw new Error(validationError);
        }

        const result = await uploadSingleFileToKnowledgeLake(file, opts);
        accepted.push(...result.accepted);
        documentIds.push(...result.documentIds);
    }

    return { accepted, documentIds };
}

/** Fire-and-forget upload into the tenant knowledge lake (does not block wizard extraction). */
export async function uploadFilesToKnowledgeLakeAsync(
    files: File[],
    options: KnowledgeLakeUploadOptions | string = "Manual upload",
): Promise<void> {
    if (!files.length) return;

    const opts: KnowledgeLakeUploadOptions =
        typeof options === "string" ? { sourceLabel: options } : { ...options, sync: false };

    try {
        await uploadFilesToKnowledgeLake(files, opts);
    } catch (e) {
        console.warn("[upload-knowledge]", e);
    }
}

/** Match an uploaded filename to a catalog row title. */
export function documentTitleMatchesUpload(
    docTitle: string,
    fileName: string,
): boolean {
    const normalize = (value: string) => value.trim().toLowerCase();
    const doc = normalize(docTitle);
    const file = normalize(fileName);
    if (doc === file) return true;
    const stripExt = (value: string) => value.replace(/\.[^.]+$/, "");
    return stripExt(doc) === stripExt(file);
}

type UploadMatchDocument = {
    id: string;
    title: string;
    sources: Array<{ sourceType?: string }>;
};

/** Whether an in-flight upload row is represented in the catalog list. */
export function uploadQueueItemMatchesDocuments(
    item: { documentId?: string; file: File },
    docs: UploadMatchDocument[],
): boolean {
    if (item.documentId) {
        return docs.some((doc) => doc.id === item.documentId);
    }

    return docs.some(
        (doc) =>
            documentTitleMatchesUpload(doc.title, item.file.name) &&
            (doc.sources[0]?.sourceType ?? "manual_upload") === "manual_upload",
    );
}

type UploadQueueReconcileItem = {
    id: string;
    status: string;
    documentId?: string;
    file: File;
};

/** Drop processing queue rows once the server catalog contains the uploaded document. */
export function reconcileUploadQueueItems<T extends UploadQueueReconcileItem>(
    queue: T[],
    docs: UploadMatchDocument[],
): T[] | null {
    let changed = false;
    const next: T[] = [];

    for (const item of queue) {
        if (item.status !== "processing") {
            next.push(item);
            continue;
        }

        if (uploadQueueItemMatchesDocuments(item, docs)) {
            changed = true;
            continue;
        }

        next.push(item);
    }

    return changed ? next : null;
}
